import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useQueries } from '@tanstack/react-query'
import KNCallToAction from 'components/KNCallToAction/KNCallToAction'
import KNLoader from 'components/KNLoader/KNLoader'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { contactsQueryOptions, lanesQueryOptions, regionsQueryOptions } from './CarrierProfile.service'
import CreateOrEditLaneDialog from './CreateOrEditLaneDialog'
import CreateOrEditRegionDialog from './CreateOrEditRegionDialog'
import LanesAndRegionsTable from './LanesAndRegionsTable'

const LanesAndRegionsTab = (): ReactElement => {
  const { t } = useTranslation()
  const [lanesQuery, regionsQuery, contactsQuery] = useQueries({
    queries: [lanesQueryOptions, regionsQueryOptions, contactsQueryOptions],
  })
  const lanesAndRegions = useMemo(
    () => [
      ...(lanesQuery.data ?? []),
      ...(regionsQuery.data ?? []),
    ],
    [lanesQuery.data, regionsQuery.data],
  )
  const contacts = contactsQuery.data ?? []
  const createLaneDialog = useDialog<typeof CreateOrEditLaneDialog>()
  const createRegionDialog = useDialog<typeof CreateOrEditRegionDialog>()

  if (lanesQuery.isPending || regionsQuery.isPending || contactsQuery.isPending) {
    return (
      <KNLoader />
    )
  }

  return (
    <>
      {lanesAndRegions.length > 0 ? (
        <>
          <Stack direction="row" spacing={1} mb={1}>
            <Button
              onClick={createLaneDialog.handleOpen}
              variant="contained"
              color="secondary"
              size="small"
            >
              {t('screens.cs.carrier_profile.lanes_and_regions.add_lane')}
            </Button>
            <Button
              onClick={createRegionDialog.handleOpen}
              variant="contained"
              color="secondary"
              size="small"
            >
              {t('screens.cs.carrier_profile.lanes_and_regions.add_region')}
            </Button>
          </Stack>
          <LanesAndRegionsTable lanesAndRegions={lanesAndRegions} contacts={contacts} />
        </>
      ) : (
        <KNCallToAction
          header={t('screens.cs.carrier_profile.lanes_and_regions.empty_header')}
          content={t('screens.cs.carrier_profile.lanes_and_regions.empty_content')}
          sx={{ minHeight: '20rem' }}
        >
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="secondary"
              onClick={createLaneDialog.handleOpen}
            >
              {t('screens.cs.carrier_profile.lanes_and_regions.add_lane')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={createRegionDialog.handleOpen}
            >
              {t('screens.cs.carrier_profile.lanes_and_regions.add_region')}
            </Button>
          </Stack>
        </KNCallToAction>
      )}

      <CreateOrEditLaneDialog
        payload={{
          contacts,
        }}
        open={createLaneDialog.isOpen}
        onAction={createLaneDialog.handleAction}
        onClose={createLaneDialog.handleClose}
      />
      <CreateOrEditRegionDialog
        payload={{
          contacts,
        }}
        open={createRegionDialog.isOpen}
        onAction={createRegionDialog.handleAction}
        onClose={createRegionDialog.handleClose}
      />
    </>
  )
}

export default LanesAndRegionsTab
